import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { DirectoryDTO } from '@/types/api-types';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { useApiManagerStore } from '@/stores/apiManagerStore';

interface AddOrEditDirectoriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingDirectories?: DirectoryDTO | null;
  onClose: () => void;
  onAdd: (directory: DirectoryDTO) => void;
  onEdit: (directory: DirectoryDTO) => void;
}

export function AddOrEditDirectoriesDialog({
  open,
  onOpenChange,
  editingDirectories,
  onClose,
  onAdd,
  onEdit,
}: AddOrEditDirectoriesDialogProps) {
  const { directories } = useApiManagerStore();

  const form = useForm<DirectoryDTO>({
    defaultValues: {
      name: editingDirectories?.name || '',
      abbreviation: editingDirectories?.abbreviation || '',
    },
  });

  const handleClose = () => {
    onClose();
    onOpenChange(false);
  };

  const handleAdd = () => {
    onAdd(form.getValues());
  };

  const handleEdit = () => {
    onEdit(form.getValues());
  };

  useEffect(() => {
    if (editingDirectories) {
      form.reset(editingDirectories);
    } else {
      form.reset({});
    }
  }, [editingDirectories, form, open]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[432px] p-0">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(
              editingDirectories ? handleEdit : handleAdd,
            )}
          >
            <DialogHeader className="p-5 pb-0">
              <div className="flex items-center justify-between">
                <DialogTitle className="font-montserrat text-2xl font-semibold text-primary">
                  {editingDirectories ? 'Редагувати дирекцію' : 'Нова дирекція'}
                </DialogTitle>
              </div>
            </DialogHeader>

            <DialogDescription hidden>
              {editingDirectories ? 'Редагувати дирекцію' : 'Нова дирекція'}
            </DialogDescription>

            <div className="space-y-4 p-4">
              <FormField
                control={form.control}
                name="abbreviation"
                rules={{
                  required: "Абревіатура є обов'язковим полем",
                  validate: (value) => {
                    // Skip validation if we're editing and the name hasn't changed
                    if (
                      editingDirectories &&
                      editingDirectories.abbreviation === value
                    ) {
                      return true;
                    }

                    // Check if the name is unique among existing indexes
                    const nameExists = directories.some(
                      (dir) =>
                        dir.abbreviation.toLowerCase().trim() ===
                        value.toLowerCase().trim(),
                    );
                    return nameExists
                      ? 'Дирекція з такою абревіатурою вже існує'
                      : true;
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Абревіатура"
                        className="h-[50px] rounded-md border-separator"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                rules={{
                  required: "Абревіатура є обов'язковим полем",
                  validate: (value) => {
                    // Skip validation if we're editing and the name hasn't changed
                    if (
                      editingDirectories &&
                      editingDirectories.name === value
                    ) {
                      return true;
                    }

                    // Check if the name is unique among existing indexes
                    const nameExists = directories.some(
                      (dir) =>
                        dir.name.toLowerCase().trim() ===
                        value.toLowerCase().trim(),
                    );
                    return nameExists
                      ? 'Дирекція з такою назвою вже існує'
                      : true;
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Назва"
                        className="h-[50px] rounded-md border-separator"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  variant="outline"
                  disabled={!form.formState.isValid}
                >
                  Зберегти
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
